import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { EulaService, OverlayDialogService } from '@services';
import { ErrorViewComponent } from 'app/components/error-view/error-view.component';
import { catchError, Observable, of, switchMap } from 'rxjs';

import { LoggerService } from '../../services/logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class EulaGuard implements CanActivate {
  private eulaService: EulaService = inject(EulaService);
  private overlayDialogService: OverlayDialogService = inject(OverlayDialogService);
  private logger = inject(LoggerService).setContext('EulaGuard');

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const usePost = route.queryParamMap.get('use-post') !== null;

    if (this.eulaService.isCurrentEulaAcceptedInSession()) {
      this.logger.info('EULA already accepted in session');
      return of(true);
    }

    return this.eulaService.checkUserEulaAcceptance().pipe(
      switchMap((eulaCheckResponse) => {
        // User already accepted the current EULA
        if (this.eulaService.isCurrentEulaAccepted(eulaCheckResponse)) {
          this.logger.info('EULA already accepted');
          return of(true);
        }

        // User hasn't accepted yet
        return this.eulaService.showEulaDialog(eulaCheckResponse, usePost).pipe(
          switchMap((accepted) => {
            if (!accepted) {
              // User declined the EULA
              this.logger.warn('User declined EULA');
              this.overlayDialogService.open(ErrorViewComponent, {
                data: { option: 'not-allowed' }
              });
              return of(false);
            }
            this.logger.info('User accepted EULA');
            return of(true);
          })
        );
      }),
      catchError((error) => {
        this.logger.error('Exception during EULA check:', error);
        this.overlayDialogService.open(ErrorViewComponent, {
          data: { option: 'something-went-wrong' }
        });
        return of(false);
      })
    );
  }
}
